<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

// // Store management imports
import { useBreakStore } from '@/stores/break';
import { useTrackerStore } from '@/stores/tracker';
import { useTimeStore } from '@/stores/time';

// // Utils imports
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
// import { logDebug } from '@/utils/logger';
import { logDebug, logError, logInfo } from '@/utils/logger';
import { toggleVideoSound } from '@/utils/videoUtils';

// // Module imports
//

// // Component imports
import BarcodeQr from '@/components/BarcodeQr/BarcodeQrComp.vue';

// // Asset imports
//

const storeBreak = useBreakStore();
const storeTracker = useTrackerStore();
const storeTime = useTimeStore();

const isKeyCurrentlyPressed = ref(false);
const hasBeenScannedSuccessfully = ref(false);
const pressedKey = ref('');

const appInstance = import.meta.env.VITE_APP_ENV;
const catchErrorPrefix = import.meta.env.VITE_APP_CATCH_ERROR_PREFIX;
// Video element references for full mode only
const isMuted = ref(true); // Start muted by default to comply with autoplay policies
const videoRef = ref(null);

const toggleSound = () => {
  toggleVideoSound(videoRef, isMuted);
};

// Time-based computed properties for conditional visibility and animations
const componentAnimationState = computed(() => {
  return storeTime.timerVisibilityState.animationState;
});

// // RightThirdScan handler functions

const keyDownHandler = (e) => {
  // logInfo(`You pressed ${e.key}: ${e.code}.`);

  CommunicationUtil.onKeyPressed(e.keyCode);
  isKeyCurrentlyPressed.value = true;
  pressedKey.value = e.key;
};

const keyUpHandler = () => {
  // logInfo(`You are no longer pressing ${e.key}: ${e.code}.`);

  isKeyCurrentlyPressed.value = false;
  pressedKey.value = '';
};

const scanHandler = (e) => {
  hasBeenScannedSuccessfully.value = e;

  console.log(`Event received from child: ${hasBeenScannedSuccessfully.value}`);
};

// // RightThirdScan lifecycle hooks

onMounted(() => {
  let videoDuration;

  document.addEventListener('keydown', keyDownHandler);
  document.addEventListener('keyup', keyUpHandler);

  // Initialize time manager for parent window communication
  // Time store will automatically detect if spoof timer should be used
  try {
    logInfo('AAAA0-TRY-RightThirdScan-onMounted');
    fetch(
      `${catchErrorPrefix}${appInstance}/appReachable?ali_test=AAAA0-TRY-RightThirdScan-onMounted`,
    );

    // Now that we know the Tizen status, start the appropriate timer
    if (
      !storeTracker.isIframe ||
      [BreakModeEnum.FULLMODE, BreakModeEnum.BROWSER].includes(storeBreak.breakDisplayMode)
    ) {
      logInfo('BBBB0-TRY-RightThirdScan-onMounted-IF-NOT-in-iframe-tizen-false');
      fetch(
        `${catchErrorPrefix}${appInstance}/appReachable?ali_test=BBBB0-TRY-RightThirdScan-onMounted-IF-NOT-in-iframe-tizen-false`,
      );

      logDebug('Using spoof timer (Tizen is false OR mode set to full/browser)');
      storeTime.startSpoofTimer(500); // Update every 500ms to simulate real behavior

      videoDuration = 60;
      storeTime.updateTimerConfig({
        fadeInStart: 8,
        fadeInEnd: 8 + 1,
        fadeOutStart: videoDuration - 3.75,
        fadeOutEnd: videoDuration - 2,
      });
    } else {
      logInfo('BBBB1-TRY-RightThirdScan-onMounted-IF-in-iframe-tizen-true');
      fetch(
        `${catchErrorPrefix}${appInstance}/appReachable?ali_test=BBBB1-TRY-RightThirdScan-onMounted-IF-in-iframe-tizen-true`,
      );

      // Use real parent window communication for production
      logDebug('Using real time manager (Tizen is true)');
      storeTime.initializeTimeManager();

      videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration);
      storeTime.updateTimerConfig({
        fadeInStart: 8,
        fadeInEnd: 8 + 1,
        fadeOutStart: videoDuration - 3.75,
        fadeOutEnd: videoDuration - 2,
      });
    }

    // logInfo('aaaa1-TRY-RightThirdScan-onMounted-pre-timer-config');
    // fetch(
    //   `${catchErrorPrefix}${appInstance}/appReachable?ali_test=aaaa1-TRY-RightThirdScan-onMounted-pre-timer-config`,
    // );
    //
    // // Apply custom timer configuration (not strictly needed for this use case, but good practice)
    // const videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration) || 60;
    // storeTime.updateTimerConfig({
    //   fadeInStart: 8,
    //   fadeInEnd: 8 + 1,
    //   fadeOutStart: videoDuration - 3.75,
    //   fadeOutEnd: videoDuration - 2,
    // });
    //
    // logInfo('aaaa1-TRY-RightThirdScan-onMounted-post-timer-config');
    // fetch(
    //   `${catchErrorPrefix}${appInstance}/appReachable?ali_test=aaaa1-TRY-RightThirdScan-onMounted-post-timer-config`,
    // );

    logInfo('AAAA1-TRY-RightThirdScan-onMounted-after-conditional');
    fetch(
      `${catchErrorPrefix}${appInstance}/appReachable?ali_test=AAAA1-TRY-RightThirdScan-onMounted-after-conditional`,
    );
  } catch (error) {
    console.error('Error initializing time manager:', error);

    logError('AAAA2-CATCH-RightThirdScan-onMounted-error-initializing-time-manager:', error);
    fetch(
      `${catchErrorPrefix}${appInstance}/appReachable?ali_test=AAAA2-CATCH-RightThirdScan-onMounted-error-initializing-time-manager&ali_error=${error}`,
    );
    // logError('aaaa3-CATCH-RightThirdScan-onMounted-error-initializing-time-manager:', error);
    // fetch(
    //   `${catchErrorPrefix}${appInstance}/appReachable?ali_test=aaaa3-CATCH-RightThirdScan-onMounted-error-initializing-time-manager&ali_error=${error}`,
    // );
    // // Continue without time manager if it fails
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
  document.removeEventListener('keyup', keyUpHandler);

  // Clean up time manager
  storeTime.stopSpoofTimer();
  storeTime.deactivateTimeManager();
});

// // RightThirdScan watchers

watch(isKeyCurrentlyPressed, (isKeyCurrentlyPressedNewVal) => {
  if (isKeyCurrentlyPressedNewVal) {
    // logInfo('RightThirdScan – Key is pressed down');
    // logInfo(`pressedKey.value: ${pressedKey.value}`);
    console.log('RightThirdScan – Key is pressed down');
    console.log(`pressedKey.value: ${pressedKey.value}`);
  }
});

watch(hasBeenScannedSuccessfully, () => {
  console.log(`Scan success?: ${hasBeenScannedSuccessfully.value}`);
});
</script>

<template>
  <section class="right-third-scan">
    <!-- Background video - Only show in full mode -->
    <video
      v-if="storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc"
      ref="videoRef"
      class="right-third-scan__background-video"
      :src="storeBreak.breakPlaybackVideoSrc"
      autoplay
      loop
      :muted="isMuted"
      playsinline
    ></video>

    <div
      v-if="storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc"
      class="right-third-scan__video-click-overlay"
      @click="toggleSound"
    ></div>
    <!-- End background video - Only show in full mode -->

    <div class="right-third-scan__container">
      <aside
        class="right-third-scan__strip"
        :class="{
          'is-clear':
            componentAnimationState === 'hidden' || componentAnimationState === 'fading-out',
          'is-opaque':
            componentAnimationState === 'visible' || componentAnimationState === 'fading-in',
        }"
      >
        <div
          v-for="item in storeBreak.breakProducts"
          :key="item.productPosition"
          class="right-third-scan__attention"
        >
          <BarcodeQr
            class="right-third-scan__qr"
            :target-url="item.productUrl"
            :interactive-break-uuid="storeBreak.breakUuid"
            :qr-success="'em'"
            :product-uuid="item.productUuid"
            :ad-id="storeBreak.breakGeneratedIdentifier"
            @scan-success="scanHandler"
          />

          <div
            class="right-third-scan__underlay"
            :class="{
              'right-third-scan__underlay--pre': !hasBeenScannedSuccessfully,
              'right-third-scan__underlay--post': hasBeenScannedSuccessfully,
            }"
          ></div>
        </div>
      </aside>
    </div>
  </section>
</template>
